import React, { useState, useEffect, useCallback, useRef } from 'react';
import { healthAPI } from '../../api/client';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

const STATUS_META = {
  ok:      { dot: 'bg-green-500',  chip: 'bg-green-50 text-green-700 border-green-200',   Icon: CheckCircleIcon,        iconColor: 'text-green-500',  label: 'OK' },
  warning: { dot: 'bg-amber-500',  chip: 'bg-amber-50 text-amber-700 border-amber-200',   Icon: ExclamationTriangleIcon, iconColor: 'text-amber-500',  label: 'Warning' },
  error:   { dot: 'bg-red-500',    chip: 'bg-red-50 text-red-700 border-red-200',         Icon: XCircleIcon,            iconColor: 'text-red-500',    label: 'Error' },
};

function fmtChecked(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function SystemHealthTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [togglingAlerts, setTogglingAlerts] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    try {
      const res = await healthAPI.getStatus(force);
      setReport(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    timer.current = setInterval(() => load(false), AUTO_REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  useEffect(() => {
    healthAPI.getSettings()
      .then((res) => setAlertsEnabled(!!res.data.alerts_enabled))
      .catch(() => {}); // non-fatal — toggle just defaults to on
  }, []);

  const toggleAlerts = async () => {
    const next = !alertsEnabled;
    setAlertsEnabled(next);       // optimistic
    setTogglingAlerts(true);
    try {
      const res = await healthAPI.updateSettings({ alerts_enabled: next });
      setAlertsEnabled(!!res.data.alerts_enabled);
    } catch (err) {
      setAlertsEnabled(!next);    // revert on failure
    } finally {
      setTogglingAlerts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={() => load(true)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
          Retry
        </button>
      </div>
    );
  }

  const overall = report?.overall_status || 'ok';
  const components = report?.components || [];
  const issues = components.filter((c) => c.status !== 'ok');

  const banner = overall === 'ok'
    ? { cls: 'bg-green-50 border-green-200 text-green-800', Icon: CheckCircleIcon, text: 'All systems operational' }
    : overall === 'warning'
    ? { cls: 'bg-amber-50 border-amber-200 text-amber-800', Icon: ExclamationTriangleIcon, text: `${issues.length} issue${issues.length !== 1 ? 's' : ''} detected` }
    : { cls: 'bg-red-50 border-red-200 text-red-800', Icon: XCircleIcon, text: `${issues.length} issue${issues.length !== 1 ? 's' : ''} detected` };

  return (
    <div className="space-y-4">
      {/* Email alerts toggle */}
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-card">
        <div>
          <p className="text-sm font-semibold text-slate-800">Email Alerts</p>
          <p className="text-xs text-slate-500 mt-0.5">
            When off, system health is still monitored but no alert emails are sent.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={alertsEnabled}
          onClick={toggleAlerts}
          disabled={togglingAlerts}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
            alertsEnabled ? 'bg-primary-600' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              alertsEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Overall banner + refresh */}
      <div className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${banner.cls}`}>
        <div className="flex items-center gap-2.5">
          <banner.Icon className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-semibold">{banner.text}</p>
            <p className="text-xs opacity-70">
              Last checked {fmtChecked(report?.generated_at)} · auto-refreshes every 5 min
            </p>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 border border-white/60 rounded-lg text-sm font-medium hover:bg-white transition-colors disabled:opacity-60"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {/* Component cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {components.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.ok;
          return (
            <div key={c.component} className="bg-white rounded-2xl border border-slate-100 shadow-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{c.component}</p>
                    <p className="text-sm text-slate-500">{c.message}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border flex-shrink-0 ${meta.chip}`}>
                  <meta.Icon className={`w-3.5 h-3.5 ${meta.iconColor}`} /> {meta.label}
                </span>
              </div>

              {/* Per-item details (email accounts / calendar connections) */}
              {Array.isArray(c.details) && c.details.length > 0 && (
                <div className="mt-3 pl-5 space-y-1 border-t border-slate-50 pt-2">
                  {c.details.map((d, i) => {
                    const dm = STATUS_META[d.status] || STATUS_META.ok;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dm.dot}`} />
                        <span className="font-medium text-slate-600 truncate max-w-[45%]">{d.name}</span>
                        <span className="text-slate-400 truncate">{d.message}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="mt-2 text-[11px] text-slate-400">Checked {fmtChecked(c.last_checked)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
